import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Input, Textarea, Select, Field } from '@/components/ui/Field';
import { Button } from '@/components/ui/Button';
import { MediaField } from '@/components/forms/MediaField';
import { MarkdownField } from '@/components/forms/MarkdownField';
import { slugify } from '@/lib/utils';
import type { PostRow } from '@/lib/admin-queries';

const CATEGORIES = [
  'Technology',
  'AI',
  'Software Engineering',
  'Cloud',
  'Startups',
  'Cybersecurity',
  'Programming',
  'Mobile',
  'Web',
  'Digital Transformation',
] as const;
const STATUSES = ['draft', 'scheduled', 'published', 'archived'] as const;

const schema = z.object({
  slug: z.string().regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and hyphens only.'),
  title: z.string().min(2),
  category: z.enum(CATEGORIES),
  status: z.enum(STATUSES),
});

interface Props {
  value: PostRow;
  submitting: boolean;
  onSubmit: (v: PostRow) => void | Promise<void>;
  onCancel: () => void;
}

export function BlogEditForm({ value, submitting, onSubmit, onCancel }: Props) {
  const [v, setV] = useState<PostRow>(value);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tagInput, setTagInput] = useState('');

  function patch<K extends keyof PostRow>(key: K, val: PostRow[K]) {
    setV((cur) => ({ ...cur, [key]: val }));
  }

  function onTitleBlur() {
    if (!v.slug || v.slug === slugify(v.title).slice(0, -1)) {
      patch('slug', slugify(v.title));
    } else if (v.slug === '') {
      patch('slug', slugify(v.title));
    }
  }

  function addTag() {
    const t = tagInput.trim();
    if (!t) return;
    if (!v.tags.includes(t)) patch('tags', [...v.tags, t]);
    setTagInput('');
  }

  function removeTag(t: string) {
    patch('tags', v.tags.filter((x) => x !== t));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    const parsed = schema.safeParse(v);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    // Auto-set published_at when moving to published.
    if (v.status === 'published' && !v.published_at) {
      patch('published_at', new Date().toISOString());
    }
    onSubmit(v);
  }

  return (
    <form id="post-form" onSubmit={handleSubmit} className="space-y-6">
      <Field id="title" label="Title" required error={errors.title}>
        {({ id, invalid }) => (
          <Input
            id={id}
            required
            invalid={invalid}
            value={v.title}
            onChange={(e) => patch('title', e.target.value)}
            onBlur={onTitleBlur}
          />
        )}
      </Field>
      <Field id="slug" label="Slug" required error={errors.slug}>
        {({ id, invalid }) => (
          <Input
            id={id}
            required
            invalid={invalid}
            value={v.slug}
            onChange={(e) => patch('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
          />
        )}
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field id="category" label="Category" required>
          {({ id }) => (
            <Select id={id} value={v.category} onChange={(e) => patch('category', e.target.value as PostRow['category'])}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          )}
        </Field>
        <Field id="status" label="Status" required>
          {({ id }) => (
            <Select id={id} value={v.status} onChange={(e) => patch('status', e.target.value as PostRow['status'])}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <Field id="excerpt" label="Excerpt" hint="≤ 200 chars · used in cards.">
        {({ id }) => (
          <Textarea id={id} rows={3} maxLength={200} value={v.excerpt ?? ''} onChange={(e) => patch('excerpt', e.target.value)} />
        )}
      </Field>

      <Field id="hero" label="Hero image">
        <MediaField value={v.hero_media_id} onChange={(m) => patch('hero_media_id', m)} bucket="public" />
      </Field>

      <Field id="body" label="Body" hint="Markdown · GitHub-flavoured.">
        <MarkdownField value={v.body_markdown} onChange={(s) => patch('body_markdown', s)} rows={24} />
      </Field>

      <div className="space-y-2">
        <label className="text-xs font-medium text-fg-muted">Tags</label>
        <div className="flex flex-wrap gap-1.5">
          {v.tags.map((t) => (
            <button
              type="button"
              key={t}
              onClick={() => removeTag(t)}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs text-fg hover:border-danger/40 hover:text-danger"
            >
              {t} <span aria-hidden>×</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Add tag (e.g. React)"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
          />
          <Button type="button" variant="secondary" onClick={addTag}>
            Add
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field id="meta_title" label="Meta title" hint="≤ 60 chars">
          {({ id }) => (
            <Input id={id} maxLength={80} value={v.meta_title ?? ''} onChange={(e) => patch('meta_title', e.target.value)} />
          )}
        </Field>
        <Field id="meta_description" label="Meta description" hint="≤ 160 chars">
          {({ id }) => (
            <Input id={id} maxLength={200} value={v.meta_description ?? ''} onChange={(e) => patch('meta_description', e.target.value)} />
          )}
        </Field>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" form="post-form" loading={submitting}>
          {v.id ? 'Save changes' : 'Create post'}
        </Button>
      </div>
    </form>
  );
}